import React, { useEffect, useState } from "react";
import { ControlData } from "../../../../../model/manifest/ControlData";
import { IBasePickerStyles, IBasePickerSuggestionsProps, IInputProps, ITag, TagPicker, TooltipHost, useTheme } from "@fluentui/react";
import { ConnectForm } from "../../../form/ConnectForm";
import { Controller, FieldValues, UseFormReturn } from "react-hook-form";
import { useRecoilValue } from "recoil";
import { applicationManifestState, globalBusinessRulesState } from "../../../../../recoil/atoms";
import { ApplicationManifest } from "../../../../../model/manifest/ApplicationManifest";
import { getRules } from "../../../../../services/ruleService";
import { BusinessRule } from "../../../../../model/manifest/BusinessRule";
import { useId } from "@fluentui/react-hooks";
import { buildFormPropertyName, getFormattedTags, readOnlyTextForDataSource } from "../../../../../services/fieldService";
import { ErrorMessage } from "@hookform/error-message";
import { addDefaultSearchTextValue, getDataSourceItems } from "../../../../../services/dataSourceService";
import { DataSourceItem } from "../../../../../model/manifest/DataSourceItem";
import axios from "axios";
import { ViewDropdownRecord } from "../helpers/ViewDropdownRecord";
import { ReadOnlyDropdownField } from "../../ReadOnlyDropdownField";
import { ISubFormField } from "../../../../../interfaces/ISubFormField";
import { cloneDeep } from "lodash";

export interface TypeAndSearchFieldProps {
    control: ControlData;
    shouldValidate: boolean;
    disabled: boolean;
    readOnly: boolean;
    isRequired: boolean;
    isSearch: boolean;
    entityId: string | undefined;
    subFormField: ISubFormField | undefined;
}

export const TypeAndSearchField: React.FC<TypeAndSearchFieldProps> = (props: TypeAndSearchFieldProps) => {
    const applicationManifest: ApplicationManifest = useRecoilValue(applicationManifestState);
    const globalBusinessRules: BusinessRule[] = useRecoilValue(globalBusinessRulesState);
    const [dataSourceItems, setDataSourceItems] = useState<DataSourceItem[]>();
    const [selectableTags, setSelectableTags] = useState<ITag[]>();
    const [textFilterLength, setTextFilterLength] = useState<number>(0);
    const CancelToken = axios.CancelToken;
    const source = CancelToken.source();
    const theme = useTheme();
    const dropdownId = useId("dropdown");
    const tooltipId = useId("tooltip");
    const calloutProps = {
        gapSpace: 0,
        target: `#${dropdownId}`
    };

    useEffect(() => {
        getDataSourceItems(applicationManifest.Id!, props.control.PageId, props.control.Id!, props.isSearch, source.token)
            .then((result) => {
                setDataSourceItems(result);
            })
            .catch((error) => {

            });
        return function cleanup() {
            source.cancel();
            setDataSourceItems([]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    useEffect(() => {
        if (dataSourceItems == null) {
            return;
        }

        //build out formatted tags
        var formattedTags = getFormattedTags(applicationManifest.DataSources.find(i => i.Name === props.control.DataSourceName)!, props.entityId, dataSourceItems);
        //add default search text value if needed
        formattedTags = addDefaultSearchTextValue(formattedTags, props.control, props.isSearch);
        setSelectableTags(formattedTags);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataSourceItems]);

    const filterSuggestedTags = async (filterText: string, tagList: ITag[]): Promise<ITag[]> => {
        setTextFilterLength(filterText == null ? 0 : filterText.length);
        return selectableTags != null && filterText && filterText.length > 2
            ? selectableTags.filter(
                tag => tag.name.toLowerCase().indexOf(filterText.toLowerCase()) === 0,
            )
            : [];
    };

    const getDefaultValue = (value: string | undefined): ITag[] | undefined => {
        if (selectableTags == null || selectableTags.length === 0 || value == null || value === "") {
            return [];
        }
        var selectedTag = selectableTags.find(i => i.key === value);
        if (selectedTag == null) {
            return [];
        }
        var tags: ITag[] = [];
        var tag: ITag = {
            key: value,
            name: selectedTag.name
        }
        tags.push(tag);
        return tags;
    };

    const getTextFromItem = (item: ITag) => item.name;

    const pickerSuggestionsProps = (): IBasePickerSuggestionsProps => {
        var basePickerSuggestions: IBasePickerSuggestionsProps = {
            suggestionsHeaderText: "Suggested items",
            noResultsFoundText: textFilterLength >= 3 ? "No matching items found" : "Minimum of three characters needed",
        };
        return basePickerSuggestions;
    };

    const inputProps: IInputProps = {
        "aria-label": "Tag picker",
    };

    const pickerStyles: IBasePickerStyles = {
        root: {
            background: theme.semanticColors.bodyBackground,
        },
        text: {},
        itemsWrapper: {},
        input: {},
        screenReaderText: {}
    };

    return (
        <ConnectForm>
            {(methods: UseFormReturn<FieldValues, any, undefined>) =>
                <Controller
                    control={methods.control}
                    rules={getRules(applicationManifest, props.control, globalBusinessRules, props.shouldValidate, props.entityId == null)}
                    name={buildFormPropertyName(props.control.Id!, false, undefined, props.subFormField)}
                    render={({ field: { onChange, value } }) => (
                        <>
                            {(!props.readOnly && selectableTags !== undefined) &&
                                <TooltipHost
                                    content={props.control.ToolTipText}
                                    id={tooltipId}
                                    calloutProps={calloutProps}
                                >
                                    <ViewDropdownRecord
                                        control={props.control}
                                        value={value}
                                        dropdownId={dropdownId}
                                        isRequired={props.isRequired}
                                    />
                                    <div id={dropdownId}>
                                        <TagPicker
                                            className={methods.formState.errors[props.control.Id!]?.message != null ? "PickerFieldErrorBorder" : ""}
                                            disabled={props.disabled}
                                            styles={pickerStyles}
                                            resolveDelay={props.control.TypeAndSearchOnServer ? 300 : undefined}
                                            onResolveSuggestions={(filter: string, selectedItems?: ITag[] | undefined) => {
                                                return filterSuggestedTags(filter, selectedItems !== undefined ? selectedItems : []);
                                            }}
                                            getTextFromItem={getTextFromItem}
                                            pickerSuggestionsProps={pickerSuggestionsProps()}
                                            itemLimit={1}
                                            inputProps={inputProps}
                                            selectedItems={getDefaultValue(value)}
                                            onChange={(items?: ITag[] | undefined) => {
                                                var myValue = items != null && items.length > 0 ? items[0].key : "";
                                                if (props.subFormField != null) {
                                                    var myCurrentValue = methods.getValues(props.subFormField.ParentControlId)[props.subFormField.Index];
                                                    var updateableField = cloneDeep(myCurrentValue);
                                                    updateableField[props.control.Id!] = myValue;
                                                    props.subFormField.update(props.subFormField.Index, updateableField);
                                                }
                                                onChange(myValue);
                                            }}
                                        />
                                    </div>
                                    <div className="CustomError">
                                        <ErrorMessage errors={methods.formState.errors} name={props.control.Id!} />
                                    </div>
                                </TooltipHost>
                            }
                            {props.readOnly &&
                                <ReadOnlyDropdownField
                                    control={props.control}
                                    textValue={readOnlyTextForDataSource(
                                        value,
                                        dataSourceItems
                                    )}
                                    value={value}
                                />
                            }
                        </>
                    )} />
            }
        </ConnectForm>
    );
}